import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { COMPUTE_DEBUG_NAMESPACE_FUNCTION, Debug, DebugHandler } from "@cervval.js/common/src/tools/debug.decorator";
import { attachTemplateAndStyle } from "./customElement.tools";
import { CUSTOM_ELEMENT_CLASS_CONFIG, CustomElementClassConfig, OverrideCustomElementConfig, WAIT_FOR_AFTER_INIT_RESOLVER, WebComponentConnectionListener } from "./customElement.types";
import { configureComponentWithDefaultDebugOptions } from "./webComponent.debug";

// _nextComponentNumber can be used to give a unique id to each elements in the DOM
let _nextComponentNumber = 1;

/**
 * Base class for WebComponents, to be used in combination with @CustomElement class decorator
 */
export class WebComponent<I18n = unknown> extends HTMLElement {

    @Debug()
    public readonly debug!: DebugHandler;

    /**
     * Unique id of the component used to identify elements in the dom
     */
    public readonly componentNumber: number;

    /**
     * If the component is in light DOM (ie not shadow DOM), all its children are within this element
     * lightDomRoot will be 'this' webComponent once component is connected, before it is a temporary slot element
     */
    public lightDomRoot: HTMLElement | undefined;

    /**
     * @param customElementOptions this allows you to override the configuration set on the @CustomElement decorator for one specific instance
     */
    constructor(public customElementOptions?: OverrideCustomElementConfig) {
        super();
        this.componentNumber = _nextComponentNumber++;

        configureComponentWithDefaultDebugOptions(this);

        // if webComponent is decorated with @CustomElement, this will attach the provided style and template
        // the template will be attached to shadow DOM or light DOM according to component options
        const classConfig = getProp(this, CUSTOM_ELEMENT_CLASS_CONFIG) as CustomElementClassConfig | undefined;
        attachTemplateAndStyle(this, classConfig);

        // Configure waitForAfterInit promise
        this.waitForAfterInitPromise = new Promise((resolve, reject) => {
            setProp(this, WAIT_FOR_AFTER_INIT_RESOLVER, resolve);
        });
    }

    /**
     * compute the debug handler namespace
     */
    protected [COMPUTE_DEBUG_NAMESPACE_FUNCTION](): string {
        const classConfig = getProp(this, CUSTOM_ELEMENT_CLASS_CONFIG) as CustomElementClassConfig | undefined;
        return "cjs:webcomponent:" + (classConfig?.config.name ?? "") + "." + this.componentNumber;
    }

    /**
     * init is called only once, the first time the component in inserted into the dom (just before the first connectedCallback call)
     * init is called if the component is decorated with @CustomElement
     * 
     * init can return a Promise but doing so the init and refresh("init") won't be synchronous and will not be terminated after appending component to the DOM
     * we advise you not to return a Promise in simple web components, it is tolerated in pages components (WebPageComponent)
     */
    public init(): Promise<void> | void { }

    /**
     * beforeInit is triggered before init() is called if the component is decorated with @CustomElement
     */
    public onBeforeInit(): void { }

    /**
     * afterInit is triggered after init() is called if the component is decorated with @CustomElement
     */
    public onAfterInit(): void { }

    /**
     * This promise is resolved if onAfterInit was called
     */
    public waitForAfterInitPromise!: Promise<void>;

    /**
     * Refresh is used to update values and render display of component
     * @param reason why refresh was called ?
     * init (INIT_REFRESH_REASON) => first connection to DOM,
     * pageShow (PAGE_SHOW_REFRESH_REASON) => on page navigation (except first one since refresh is called with init)
     * action (ACTION_REFRESH_REASON) => on undo or redo
     * other => any application related reason
     */
    public refresh(reason?: "init" | "pageShow" | "action" | string): void { }

    /**
     * @returns the shadow DOM root if there is one, the component light DOM root otherwise
     */
    public getContainer(): ParentNode {
        if (this.shadowRoot) {
            return this.shadowRoot;
        }
        return this.lightDomRoot!;
    }

    /**
     * When component gets its content from a html file, children elements that were present in the html file will be reparented to the 'ParsedInnerHtmlTarget'
     * @returns the target component or undefined if content can stay at root of the component
     */
    public getParsedInnerHtmlTarget(): Node | undefined {
        return undefined;
    }

    /** 
     * true if the component has been connected at least once in the DOM
     * value set in @CustomElement decorator
     */
    public wasConnected = false;

    /** 
     * true if the component init method has been called
     * value set in @CustomElement decorator
     */
    public isInitDone = false;

    /** I18n of web component 
     * ! this variable is "static" and shared with all instances of the component class
     */
    public readonly i18n!: I18n;

    /** stores connectionListeners (used by customElement decorator), use addConnectionListener to add a listener */
    public connectionListeners: WebComponentConnectionListener[] | undefined;

    /** 
     * add a listener that listens to connection or disconnection of this component
     * unless a listener with the same origin is already registered
     */
    public addConnectionListener(listener: WebComponentConnectionListener): void {
        this.connectionListeners ??= [];
        if (listener.origin) {
            for (const l of this.connectionListeners) {
                if (l.origin === listener.origin) {
                    // already installed
                    return;
                }
            }
        }
        this.connectionListeners.push(listener);
    }

    /** 
     * remove listener with the provided origin
     */
    public removeConnectionListener(origin: string): void {
        if (this.connectionListeners) {
            this.connectionListeners = this.connectionListeners.filter((l) => l.origin !== origin);
        }
    }
}
